import { type FC } from 'react'
import {
  BrowserRouter,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router'

import BrokerContext from 'components/context/BrokerContext'
import { brokersWorkflows } from 'rails_routes'
import { Breadcrumbs } from 'slab'
import { useGetBrokersTaskWorkflowsId } from 'utils/web_api'

import { type Broker } from '../types'
import WorkflowDetails from './WorkflowDetails'
import WorkflowsQueryView from './WorkflowsQueryView'

type WorkflowsViewProps = {
  currentBroker?: Broker
  stripePublishableKey: string
  isUserThatchAdmin: boolean
  isUserThatchWorkflowAdmin: boolean
}

// Utility function to get member initials from full name
const getMemberInitials = (memberName: string): string => {
  const nameParts = memberName.trim().split(' ')
  if (nameParts.length === 0) return ''
  if (nameParts.length === 1) return nameParts[0].charAt(0).toUpperCase()

  const firstName = nameParts[0].charAt(0).toUpperCase()
  const lastName = nameParts[nameParts.length - 1].charAt(0).toUpperCase()
  return `${firstName}${lastName}`
}

// Utility function to format workflow type for display
const formatWorkflowType = (workflowType: string): string => {
  switch (workflowType) {
    case 'submission':
      return 'Submission'
    case 'renewal':
      return 'Renewal'
    case 'plan_change':
      return 'Plan Change'
    case 'dependent_change':
      return 'Dependent Change'
    case 'plan_change_and_dependent_change':
      return 'Plan & Dependent Change'
    case 'off_thatch':
      return 'Off Thatch'
    case 'cancellation':
      return 'Cancellation'
    default:
      return workflowType
  }
}

const WorkflowsBreadcrumbs: FC = () => {
  const location = useLocation()
  const navigate = useNavigate()

  // Extract workflow ID from path if present
  const pathSegments = location.pathname.split('/')
  const workflowId = pathSegments[3] // /brokers/workflows/:id

  const isWorkflowDetails = workflowId && pathSegments.length > 3

  // Fetch workflow data if we're on a workflow details page
  const shouldFetchWorkflow = Boolean(isWorkflowDetails && workflowId)
  const { data: workflow, isLoading: isWorkflowLoading } = useGetBrokersTaskWorkflowsId(
    workflowId || '',
    { evaluate_readiness: false }, // API params - evaluate for breadcrumb display
    {
      query: {
        enabled: shouldFetchWorkflow,
      },
    }
  )

  // Generate the workflow breadcrumb text or skeleton
  const getWorkflowBreadcrumbContent = () => {
    // Show skeleton while loading
    if (isWorkflowLoading) {
      return (
        <div className='flex items-center space-x-1'>
          <div className='h-4 w-6 bg-gray-200 rounded animate-pulse' />
          <div className='h-4 w-16 bg-gray-200 rounded animate-pulse' />
        </div>
      )
    }

    // Show formatted text if data is available
    if (workflow?.enrollment_info?.member_name && workflow?.workflow_type) {
      const initials = getMemberInitials(workflow.enrollment_info.member_name)
      const workflowType = formatWorkflowType(workflow.workflow_type)
      return `${initials} ${workflowType}`
    }

    // Fallback to workflow ID if data is not available but not loading
    return `Workflow ${workflowId}`
  }

  return (
    <div className='max-w-[2400px] mx-auto px-4 pb-2'>
      <Breadcrumbs>
        <Breadcrumbs.Item>Brokers</Breadcrumbs.Item>
        {isWorkflowDetails
          ? (
            <>
              <Breadcrumbs.Item onClick={() => navigate(brokersWorkflows.index())}>
                Workflows
              </Breadcrumbs.Item>
              <Breadcrumbs.Item current>
                {getWorkflowBreadcrumbContent()}
              </Breadcrumbs.Item>
            </>
            )
          : (
            <Breadcrumbs.Item current>Workflows</Breadcrumbs.Item>
            )}
      </Breadcrumbs>
    </div>
  )
}

const WorkflowsView: FC<WorkflowsViewProps> = ({
  currentBroker,
  stripePublishableKey,
  isUserThatchAdmin,
  isUserThatchWorkflowAdmin,
}) => {
  return (
    <BrowserRouter>
      <BrokerContext.Provider value={{ currentBroker }}>
        <div className='min-h-screen'>
          <WorkflowsBreadcrumbs />
          <Routes>
            {/* Main workflows list view */}
            <Route
              path={brokersWorkflows.index()}
              element={<WorkflowsQueryView />}
            />
            {/* Individual workflow details */}
            <Route
              path='/brokers/workflows/:id'
              element={
                <WorkflowDetails
                  stripePublishableKey={stripePublishableKey}
                  isUserThatchAdmin={isUserThatchAdmin}
                  isUserThatchWorkflowAdmin={isUserThatchWorkflowAdmin}
                />
                }
            />
          </Routes>
        </div>
      </BrokerContext.Provider>
    </BrowserRouter>
  )
}

export default WorkflowsView
